/**
 * ChallengeSettings
 *
 * Body for a `<SlideModal>` mounted from the lobby. Lets the user pick:
 *   - Timer (per-battleType presets — ST: 30/60/90, PTB: 60/120/180)
 *   - Orientation (white / black) — Square Trainer only
 *   - Mistake limit (3 / 5 / 10) — Position Trainer Battle only
 *
 * Pure controlled component — receives current values + onChange, no side
 * effects. The lobby decides when to commit (the user taps "Send Challenge").
 */

import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';

import {
  BattleType,
  type ChallengeSettings as ChallengeSettingsValue,
} from '@/Screens/FunChallenges/types';

// Per-battle-type presets. Square Trainer ships unchanged (30/60/90); PTB
// gives the player more time per puzzle since puzzles are multi-ply. PDB
// has NO timer chips — its only end condition is target-reached or
// mistake-out (the 10-min `timerSec` is a hard safety cap, not user-tunable).
const TIMER_PRESETS: Record<BattleType, number[]> = {
  [BattleType.SQUARE_TRAINER]: [30, 60, 90],
  [BattleType.POSITION_TRAINER]: [60, 120, 180],
  [BattleType.PUZZLE_DASH]: [],   // hidden — see PDB branch below
};
const MAX_WRONG_OPTIONS_PTB: number[] = [3, 5, 10];
// PDB ships an "Unlimited" option (rendered as 100, a soft cap large enough
// that a normal race ends on target_reached well before this triggers).
const MAX_WRONG_OPTIONS_PDB: number[] = [3, 5, 7, 100];
const TARGET_PRESETS: number[] = [10, 15, 20, 30];
const ORIENTATION_OPTIONS: Array<'white' | 'black'> = ['white', 'black'];

interface Props {
  value: ChallengeSettingsValue;
  onChange: (next: ChallengeSettingsValue) => void;
  battleType?: BattleType;
}

export default function ChallengeSettings({
  value,
  onChange,
  battleType = BattleType.SQUARE_TRAINER,
}: Props) {
  const timerOptions = TIMER_PRESETS[battleType] || TIMER_PRESETS[BattleType.SQUARE_TRAINER];
  const isPTB = battleType === BattleType.POSITION_TRAINER;
  const isPDB = battleType === BattleType.PUZZLE_DASH;
  const isST = !isPTB && !isPDB;
  const maxWrongOptions = isPDB ? MAX_WRONG_OPTIONS_PDB : MAX_WRONG_OPTIONS_PTB;

  const setTimer = (timerSec: number) => onChange({...value, timerSec});
  const setOrientation = (orientation: 'white' | 'black') =>
    onChange({...value, orientation});
  const setMaxWrong = (maxWrong: number) => onChange({...value, maxWrong});
  const setTargetCorrect = (targetCorrect: number) =>
    onChange({...value, targetCorrect});

  return (
    <Box style={styles.root}>
      {/* PDB hides the timer entirely — its only primary end condition is
          target-reached or mistake-out. The 10-min `timerSec` ships as a
          hard safety cap and is not user-tunable. */}
      {!isPDB && (
        <Section title="Time control">
          <View style={styles.chipRow}>
            {timerOptions.map(sec => {
              const selected = value.timerSec === sec;
              return (
                <Pressable
                  key={sec}
                  onPress={() => setTimer(sec)}
                  style={[styles.chip, selected && styles.chipSelected]}>
                  <CCText
                    style={[
                      styles.chipText,
                      selected && styles.chipTextSelected,
                    ]}>
                    {sec}s
                  </CCText>
                </Pressable>
              );
            })}
          </View>
        </Section>
      )}

      {isPDB && (
        <Section title="First to">
          <View style={styles.chipRow}>
            {TARGET_PRESETS.map(n => {
              const selected = (value.targetCorrect ?? 15) === n;
              return (
                <Pressable
                  key={n}
                  onPress={() => setTargetCorrect(n)}
                  style={[styles.chip, selected && styles.chipSelected]}>
                  <CCText
                    style={[
                      styles.chipText,
                      selected && styles.chipTextSelected,
                    ]}>
                    {n} correct
                  </CCText>
                </Pressable>
              );
            })}
          </View>
        </Section>
      )}

      {isST && (
        <Section title="Orientation">
          <View style={styles.chipRow}>
            {ORIENTATION_OPTIONS.map(o => {
              const selected = value.orientation === o;
              return (
                <Pressable
                  key={o}
                  onPress={() => setOrientation(o)}
                  style={[styles.chip, selected && styles.chipSelected]}>
                  <CCText
                    style={[
                      styles.chipText,
                      selected && styles.chipTextSelected,
                    ]}>
                    {o[0].toUpperCase() + o.slice(1)}
                  </CCText>
                </Pressable>
              );
            })}
          </View>
        </Section>
      )}

      {(isPTB || isPDB) && (
        <Section title="Mistake limit">
          <View style={styles.chipRow}>
            {maxWrongOptions.map(n => {
              const selected = (value.maxWrong ?? 5) === n;
              // PDB chip value of 100 is the "Unlimited" affordance —
              // surface it that way in the UI so the user doesn't read
              // "100 wrong" literally.
              const label = isPDB && n >= 100 ? 'Unlimited' : `${n} wrong`;
              return (
                <Pressable
                  key={n}
                  onPress={() => setMaxWrong(n)}
                  style={[styles.chip, selected && styles.chipSelected]}>
                  <CCText
                    style={[
                      styles.chipText,
                      selected && styles.chipTextSelected,
                    ]}>
                    {label}
                  </CCText>
                </Pressable>
              );
            })}
          </View>
        </Section>
      )}
    </Box>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.section}>
      <CCText style={styles.sectionTitle}>{title}</CCText>
      {children}
    </View>
  );
}

export const DEFAULT_CHALLENGE_SETTINGS: ChallengeSettingsValue = {
  timerSec: 60,
  orientation: 'white',
};

/** PTB ships its own defaults — longer clock + mistake limit. */
export const DEFAULT_PTB_CHALLENGE_SETTINGS: ChallengeSettingsValue = {
  timerSec: 120,
  orientation: 'white',
  maxWrong: 5,
};

/**
 * PDB defaults — 10-min hard safety cap (not user-tunable), target-correct
 * chips drive the race end condition. `orientation` is kept for type-shape
 * parity with the other modes but unused by the PDB adapter.
 */
export const DEFAULT_PDB_CHALLENGE_SETTINGS: ChallengeSettingsValue = {
  timerSec: 600,
  orientation: 'white',
  targetCorrect: 10,
  maxWrong: 100,
};

const styles = StyleSheet.create({
  root: {paddingVertical: vs(8)},
  section: {marginBottom: vs(20)},
  sectionTitle: {
    fontSize: FontSizes[14] || 14,
    fontWeight: '600',
    marginBottom: vs(8),
    color: CCColors.GreyDarkest ?? '#444',
  },
  chipRow: {flexDirection: 'row', gap: 8, flexWrap: 'wrap'},
  chip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#2623221A',
    backgroundColor: CCColors.White,
  },
  chipSelected: {
    backgroundColor: CCColors.Primary?.Brown ?? '#4D3F37',
    borderColor: CCColors.Primary?.Brown ?? '#4D3F37',
  },
  chipText: {
    fontSize: FontSizes[14] || 14,
    color: CCColors.GreyDarkest ?? '#444',
  },
  chipTextSelected: {
    color: CCColors.White,
    fontWeight: '600',
  },
});
